//src/services/oekobaudatService.js
import { extractFlowProperties } from '../utils/okobaudatProperties';

const PROXY_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? '/api/oekobaudat'
    : (process.env.REACT_APP_PROXY_TARGET || 'https://embodix.vercel.app/api/oekobaudat');

const RESOURCE_CACHE = new Map();
const PROCESS_DETAIL_CACHE = new Map();

const SUPERSCRIPT_MAP = {
  '0': String.fromCharCode(0x2070),
  '1': String.fromCharCode(0x00B9),
  '2': String.fromCharCode(0x00B2),
  '3': String.fromCharCode(0x00B3),
  '4': String.fromCharCode(0x2074),
  '5': String.fromCharCode(0x2075),
  '6': String.fromCharCode(0x2076),
  '7': String.fromCharCode(0x2077),
  '8': String.fromCharCode(0x2078),
  '9': String.fromCharCode(0x2079),
  '-': String.fromCharCode(0x207B)
};

function superscriptDigits(str) {
  return str.replace(/[0-9-]/g, (ch) => SUPERSCRIPT_MAP[ch] || ch);
}

const UNIT_SYMBOL_MAP = {
  qm: 'm^2',
  m2: 'm^2',
  qkm: 'km^2',
  m3: 'm^3',
  'm^3': 'm^3',
  ha: 'ha',
  m: 'm',
  km: 'km',
  g: 'g',
  kg: 'kg',
  t: 't',
  pcs: 'pcs',
  Stk: 'pcs'
};
const ROMAN_MAP = { I: 1, II: 2, III: 3, IV: 4, V: 5 };

function toArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}
function approximatelyEqual(a, b, epsilon = 1e-6) {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= epsilon * Math.max(1, Math.abs(a), Math.abs(b));
}
function normalizeString(value) {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length ? str : null;
}
function normalizeInternalId(entry) {
  if (entry == null) return null;
  if (typeof entry === 'string' || typeof entry === 'number') return normalizeString(entry);
  if (typeof entry === 'object') {
    return (
      normalizeString(entry.dataSetInternalID) ||
      normalizeString(entry['@dataSetInternalID']) ||
      normalizeString(entry.internalID) ||
      normalizeString(entry['@internalID']) ||
      normalizeString(entry.id) ||
      normalizeString(entry['@id']) ||
      null
    );
  }
  return null;
}
function extractFlowUuid(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const candidate =
    entry.refObjectId ||
    entry.refObjectID ||
    entry.uuid ||
    entry['@uuid'] ||
    entry.referenceToFlowDataSet?.refObjectId ||
    entry.referenceToFlowDataSet?.refObjectID ||
    entry.referenceToFlow?.refObjectId ||
    entry.referenceToFlow?.refObjectID ||
    entry.referenceToFlowPropertyDataSet?.refObjectId ||
    null;
  return normalizeString(candidate);
}
function parseNumericString(raw) {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const normalized = trimmed.replace(/\s+/g, '').replace(/,/g, '.');
  const direct = Number(normalized);
  if (Number.isFinite(direct)) return direct;

  if (normalized.includes('*')) {
    const factors = normalized.split('*').map((part) => part.trim()).filter(Boolean);
    if (factors.length > 1) {
      let product = 1;
      let valid = true;
      for (const factor of factors) {
        const parsed = parseNumericString(factor);
        if (parsed == null) { valid = false; break; }
        product *= parsed;
      }
      if (valid) return product;
    }
  }

  const match = normalized.match(/[+-]?\d+(?:\.\d+)?(?:e[+-]?\d+)?/i);
  if (match) {
    const parsed = Number(match[0]);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
function extractNumericValue(node, visited = new Set()) {
  if (node == null) return null;
  if (typeof node === 'number') return Number.isFinite(node) ? node : null;
  if (typeof node === 'string') return parseNumericString(node);
  if (Array.isArray(node)) {
    for (const item of node) {
      const result = extractNumericValue(item, visited);
      if (result != null) return result;
    }
    return null;
  }
  if (typeof node !== 'object') return null;
  if (visited.has(node)) return null;
  visited.add(node);

  const priorityKeys = [
    'resultingAmount','meanAmount','meanValue','amount','quantity','value',
    'numericalValue','deterministicValue','lowerBound','upperBound','factor'
  ];

  for (const key of priorityKeys) {
    if (node[key] != null) {
      const extracted = extractNumericValue(node[key], visited);
      if (extracted != null) return extracted;
    }
    const attrKey = `@${key}`;
    if (node[attrKey] != null) {
      const extracted = extractNumericValue(node[attrKey], visited);
      if (extracted != null) return extracted;
    }
  }

  for (const [key, value] of Object.entries(node)) {
    if (!/amount|value|quantity|factor/i.test(key)) continue;
    const extracted = extractNumericValue(value, visited);
    if (extracted != null) return extracted;
  }
  return null;
}

function toCacheKey(path) { return path; }

async function fetchJSON(path, { signal } = {}) {
  const cacheKey = toCacheKey(path);
  if (!signal && RESOURCE_CACHE.has(cacheKey)) return RESOURCE_CACHE.get(cacheKey);

  const url = `${PROXY_BASE_URL}?path=${encodeURIComponent(path)}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' }, signal });

  if (!response.ok) throw new Error(`Oekobaudat request failed (${response.status})`);
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.toLowerCase().includes('application/json')) {
    throw new Error('Oekobaudat proxy returned non-JSON response');
  }
  const data = await response.json();
  if (!signal) RESOURCE_CACHE.set(cacheKey, data);
  return data;
}

function scoreDatastock(shortName = '') {
  const match = shortName.match(/OBD_(\d{4})_(I{1,3}|IV|V)?/);
  if (!match) return 0;
  const year = parseInt(match[1], 10);
  const release = match[2] ? (ROMAN_MAP[match[2]] || 0) : 0;
  return year * 10 + release;
}

function getLocalizedValue(entries, preferred = 'en') {
  if (!entries) return null;
  if (Array.isArray(entries)) {
    const preferredMatch = entries.find((entry) => entry.lang === preferred);
    if (preferredMatch?.value) return preferredMatch.value;
    const fallback = entries.find((entry) => entry.value);
    return fallback?.value ?? null;
  }
  if (typeof entries === 'object' && entries.value) return entries.value;
  return null;
}

function formatUnitName(rawName) {
  if (!rawName) return null;
  const base = UNIT_SYMBOL_MAP[rawName] || rawName;
  return base.replace(/\^(-?[0-9]+)/g, (match, digits) => superscriptDigits(digits));
}
function formatAmountLabel(value) {
  if (!Number.isFinite(value)) return null;
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs >= 1000) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs < 0.001) return value.toExponential(2);
  return value.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function pickText(node, preferredLang = 'en') {
  if (node == null) return null;
  if (typeof node === 'string' || typeof node === 'number') {
    const str = String(node).trim();
    return str.length ? str : null;
  }
  if (Array.isArray(node)) {
    const withLang = node.filter((e) => e && typeof e === 'object' && 'lang' in e && e.value);
    const preferred = withLang.find((e) => (e.lang || '').toLowerCase() === preferredLang && e.value);
    if (preferred) {
      const out = pickText(preferred.value, preferredLang);
      if (out) return out;
    }
    const fallback = withLang.find((e) => e.value);
    if (fallback) {
      const out = pickText(fallback.value, preferredLang);
      if (out) return out;
    }
    const flattened = node.map((e) => pickText(e, preferredLang)).filter(Boolean);
    return flattened.length ? flattened.join('\n\n') : null;
  }
  if (typeof node === 'object') {
    if (typeof node.value === 'string' || typeof node.value === 'number' || Array.isArray(node.value)) {
      const out = pickText(node.value, preferredLang);
      if (out) return out;
    }
    if (typeof node.text === 'string') {
      const txt = node.text.trim();
      if (txt) return txt;
    }
    if (node.shortDescription) {
      const out = pickText(node.shortDescription, preferredLang);
      if (out) return out;
    }
    if (node.description) {
      const out = pickText(node.description, preferredLang);
      if (out) return out;
    }
    if (node.comment) {
      const out = pickText(node.comment, preferredLang);
      if (out) return out;
    }
    if (node.statementOfQualifiedReviewer) {
      const out = pickText(node.statementOfQualifiedReviewer, preferredLang);
      if (out) return out;
    }
    const strings = Object.keys(node)
      .filter((k) => !['lang','id','module','type','refObjectId','version','uri'].includes(k))
      .map((k) => pickText(node[k], preferredLang)).filter(Boolean);
    if (strings.length) return strings.join('\n');
  }
  return null;
}

const SUBTYPE_CATEGORIES = [
  'Generic dataset','Representative dataset','Average dataset','Specific dataset','Template dataset'
];

function extractLocalizedProcessName(processDetail, fallbackName) {
  if (!processDetail) return fallbackName ?? null;

  const enPreferred =
    getLocalizedValue(processDetail?.processInformation?.dataSetInformation?.name?.baseName, 'en') ||
    getLocalizedValue(processDetail?.processInformation?.dataSetInformation?.name, 'en') ||
    getLocalizedValue(processDetail?.name?.baseName, 'en') ||
    getLocalizedValue(processDetail?.name, 'en') ||
    pickText(processDetail?.processInformation?.dataSetInformation?.name?.baseName, 'en') ||
    pickText(processDetail?.processInformation?.dataSetInformation?.name, 'en') ||
    pickText(processDetail?.name?.baseName, 'en') ||
    pickText(processDetail?.name, 'en');

  if (enPreferred && enPreferred.trim()) return enPreferred.trim();

  const fallback =
    typeof processDetail?.processInformation?.dataSetInformation?.name === 'string'
      ? processDetail.processInformation.dataSetInformation.name
      : typeof processDetail?.name === 'string'
        ? processDetail.name
        : fallbackName ?? null;

  return fallback ? String(fallback).trim() : null;
}

function normalizeTextForSearch(raw) {
  if (!raw) return '';
  if (Array.isArray(raw)) {
    return raw.map((r) => (typeof r === 'string' ? r : (pickText(r, 'en') || ''))).join(' ').toLowerCase();
  }
  if (typeof raw === 'object') return pickText(raw, 'en')?.toLowerCase() || '';
  return String(raw).toLowerCase();
}

function extractSubtypeFromProcess(process) {
  const candidatePaths = [
    process?.subType,
    process?.subtype,
    process?.processInformation?.dataSetInformation?.typeOfDataSet,
    process?.modellingAndValidation?.LCIMethodAndAllocation?.other,
    process?.modellingAndValidation?.validation?.other,
    process?.other,
    process?.processInformation?.dataSetInformation?.generalComment,
    process?.documentation,
    process?.modellingAndValidation
  ];

  const combined = candidatePaths.map(normalizeTextForSearch).filter(Boolean).join(' ');
  if (!combined) return null;

  const found = SUBTYPE_CATEGORIES.find((cat) => combined.includes(cat.toLowerCase()));
  return found || null;
}

function formatMethodologyNotes(other) {
  const entries = other?.anies;
  if (!Array.isArray(entries)) return null;
  const notes = entries.map((entry) => pickText(entry, 'en')).filter(Boolean);
  return notes.length ? notes.join('\n\n') : null;
}
function formatReviewDetails(details) {
  if (!details) return null;
  const parts = [];
  const statement = pickText(details.statementOfQualifiedReviewer, 'en');
  if (statement) parts.push(statement);

  if (Array.isArray(details.reviewer)) {
    const reviewerLines = details.reviewer
      .map((r) => {
        const name = pickText(r.name, 'en') || r.name || r.id;
        const organisation = pickText(r.organisation?.name, 'en');
        const role = r.reviewType;
        return [name, organisation, role].filter(Boolean).join(' - ');
      })
      .filter(Boolean);
    if (reviewerLines.length) parts.push('Reviewers\n' + reviewerLines.map((l) => '- ' + l).join('\n'));
  }
  return parts.length ? parts.join('\n\n') : null;
}
function formatComplianceDeclarations(list) {
  if (!Array.isArray(list)) return null;
  const lines = list
    .map((entry) => {
      const system = pickText(entry?.referenceToComplianceSystem?.shortDescription, 'en');
      const approval = entry?.approvalOfOverallCompliance;
      return [system, approval].filter(Boolean).join(' - ');
    })
    .filter(Boolean);
  return lines.length ? lines.join('\n') : null;
}
function extractDocumentation(processDetail) {
  const docFields = {
    generalDescription: pickText(processDetail?.processInformation?.dataSetInformation?.generalComment, 'en'),
    technologyDescription: pickText(processDetail?.processInformation?.technology?.technologyDescriptionAndIncludedProcesses, 'en'),
    useAdvice: pickText(processDetail?.modellingAndValidation?.validation?.useAdviceForDataSet, 'en'),
    methodologyNotes: formatMethodologyNotes(processDetail?.modellingAndValidation?.LCIMethodAndAllocation?.other),
    reviewDetails: formatReviewDetails(processDetail?.modellingAndValidation?.validation?.reviewDetails),
    compliance: formatComplianceDeclarations(processDetail?.modellingAndValidation?.validation?.complianceDeclarations?.compliance)
  };

  const normalized = Object.fromEntries(
    Object.entries(docFields).map(([key, value]) => {
      if (typeof value === 'string') {
        const cleaned = value.replace(/\r?\n/g, '\n').trim();
        return [key, cleaned.length ? cleaned : null];
      }
      return [key, value ?? null];
    })
  );

  return { ...normalized, hasContent: Object.values(normalized).some(Boolean) };
}

function parseImpactResults(lciaResults = []) {
  return lciaResults
    .map((item) => {
      const desc = getLocalizedValue(item?.referenceToLCIAMethodDataSet?.shortDescription) || 'Impact category';
      const unitEntry = item?.other?.anies?.find((e) => e.name === 'referenceToUnitGroupDataSet');
      const unit = getLocalizedValue(unitEntry?.value?.shortDescription) || null;
      const modules = {};
      (item?.other?.anies || []).forEach((e) => {
        if (e.module) {
          const numericValue = parseFloat(e.value);
          if (!Number.isNaN(numericValue)) modules[e.module] = numericValue;
        }
      });
      const total = Object.values(modules).reduce((acc, v) => acc + v, 0);
      return {
        id: item?.referenceToLCIAMethodDataSet?.refObjectId || desc,
        description: desc,
        unit,
        modules,
        total
      };
    })
    .filter((impact) => Object.keys(impact.modules).length > 0);
}

function extractManufacturer(processDetail, fallback) {
  const publicationOwner = getLocalizedValue(
    processDetail?.administrativeInformation?.publicationAndOwnership?.referenceToOwnershipOfDataSet?.shortDescription
  );
  const dataGenerator = getLocalizedValue(
    processDetail?.administrativeInformation?.dataGenerator?.referenceToDataSet?.shortDescription
  );
  return publicationOwner || dataGenerator || fallback || null;
}

async function resolveDeclaredUnit(processDetail) {
  const referenceFlowEntries = toArray(
    processDetail?.processInformation?.quantitativeReference?.referenceToReferenceFlow
  );
  const exchanges = toArray(processDetail?.exchanges?.exchange);

  let referenceFlowEntry = null;
  let referenceExchange = null;

  for (const entry of referenceFlowEntries) {
    const entryId = normalizeInternalId(entry);
    const entryUuid = extractFlowUuid(entry);

    const match = exchanges.find((exchange) => {
      const exchangeId = normalizeInternalId(exchange?.dataSetInternalID) || normalizeInternalId(exchange?.internalID);
      const exchangeUuid = extractFlowUuid(exchange?.referenceToFlowDataSet) || extractFlowUuid(exchange);
      if (entryId && exchangeId && entryId === exchangeId) return true;
      if (entryUuid && exchangeUuid && entryUuid === exchangeUuid) return true;
      return false;
    });

    if (match) { referenceFlowEntry = entry; referenceExchange = match; break; }
  }

  if (!referenceExchange && exchanges.length === 1) {
    referenceFlowEntry = referenceFlowEntries[0] || null;
    referenceExchange = exchanges[0];
  }
  if (!referenceExchange) return null;

  let flowUuid = extractFlowUuid(referenceExchange?.referenceToFlowDataSet) || extractFlowUuid(referenceExchange);
  if (!flowUuid) flowUuid = extractFlowUuid(referenceFlowEntry);
  if (!flowUuid) return null;

  const flow = await fetchJSON(`flows/${flowUuid}?format=json`);
  const flowInfo = flow?.flowInformation;
  const flowPropsRoot = flow?.flowProperties;
  const refFlowPropertyId = flowInfo?.quantitativeReference?.referenceToReferenceFlowProperty;
  const flowProperties = (flowPropsRoot?.flowProperty) || (flowInfo?.flowProperty) || [];
  const flowPropertyEntry = flowProperties.find((p) => p.dataSetInternalID === refFlowPropertyId) || flowProperties[0];
  const flowPropertyUuid = flowPropertyEntry?.referenceToFlowPropertyDataSet?.refObjectId;
  const referencePropertyName = getLocalizedValue(
    flowPropertyEntry?.referenceToFlowPropertyDataSet?.shortDescription
  );
  const flowPropertyAmount = extractNumericValue(flowPropertyEntry?.meanValue ?? flowPropertyEntry);

  let unitName = null;
  if (flowPropertyUuid) {
    const flowProperty = await fetchJSON(`flowproperties/${flowPropertyUuid}?format=json`);
    const unitGroupUuid = flowProperty?.flowPropertiesInformation?.quantitativeReference?.referenceToReferenceUnitGroup?.refObjectId;
    const unitGroup = unitGroupUuid ? await fetchJSON(`unitgroups/${unitGroupUuid}?format=json`) : null;
    const referenceUnitId = unitGroup?.unitGroupInformation?.quantitativeReference?.referenceToReferenceUnit;
    const units = unitGroup?.units?.unit || [];
    const unitEntry = units.find((u) => u.dataSetInternalID === referenceUnitId) || units[0];
    unitName = formatUnitName(unitEntry?.name);
  }

  const amountCandidates = [
    referenceExchange?.resultingAmount,
    referenceExchange?.meanAmount,
    referenceExchange?.amount,
    referenceFlowEntry?.resultingAmount,
    referenceFlowEntry?.meanAmount,
    referenceFlowEntry?.amount,
    processDetail?.processInformation?.quantitativeReference?.referenceFunction,
    processDetail?.processInformation?.quantitativeReference,
    referenceFlowEntry,
    referenceExchange
  ];

  let amount = null;
  for (const c of amountCandidates) {
    const extracted = extractNumericValue(c);
    if (extracted != null) { amount = extracted; break; }
  }

  const normalizedAmount = Number.isFinite(amount) ? amount : null;
  const propertyAmount = Number.isFinite(flowPropertyAmount) ? flowPropertyAmount : null;
  let resolvedAmount = normalizedAmount;

  if (propertyAmount != null) {
    if (resolvedAmount == null) {
      resolvedAmount = propertyAmount;
    } else if (approximatelyEqual(resolvedAmount, 1) && !approximatelyEqual(propertyAmount, resolvedAmount)) {
      resolvedAmount = propertyAmount;
    }
  }

  const flowNameCandidates = [
    pickText(flowInfo?.dataSetInformation?.name?.baseName, 'en'),
    pickText(flowInfo?.dataSetInformation?.name, 'en'),
    getLocalizedValue(referenceExchange?.referenceToFlowDataSet?.shortDescription),
    pickText(referenceExchange?.referenceToFlowDataSet?.name, 'en'),
    pickText(referenceExchange?.referenceToFlowDataSet, 'en')
  ].filter(Boolean);

  const rawFlowName = flowNameCandidates[0] || null;
  const amountLabel = formatAmountLabel(resolvedAmount);
  const quantityDescriptor = [amountLabel, unitName].filter(Boolean).join(' ');
  const detailSegments = [];
  if (quantityDescriptor) detailSegments.push(quantityDescriptor);
  if (referencePropertyName) detailSegments.push(referencePropertyName);
  const detailSuffix = detailSegments.length ? ` (${detailSegments.join(' • ')})` : '';
  const displayFlowName = rawFlowName
    ? `${rawFlowName}${detailSuffix}`
    : detailSegments.length
      ? detailSegments.join(' • ')
      : null;

  return {
    flowName: displayFlowName,
    property: referencePropertyName,
    unit: unitName || 'declared unit',
    amount: resolvedAmount,
    flowData: flow,
    rawFlowName
  };
}

export async function fetchLatestDatastock(signal) {
  const data = await fetchJSON('datastocks?format=json', { signal });
  const stocks = Array.isArray(data?.dataStock) ? data.dataStock : [];
  if (stocks.length === 0) throw new Error('No Oekobaudat datastocks available.');

  const sorted = [...stocks]
    .filter((stock) => typeof stock?.uuid === 'string')
    .sort((a, b) => scoreDatastock(b.shortName) - scoreDatastock(a.shortName));

  return sorted[0] || stocks[0];
}

export async function searchProcesses(
  stockUuid,
  query,
  { signal, startIndex = 0, pageSize = 40, enrich = false } = {}
) {
  const encodedQuery = encodeURIComponent(query.trim());
  const path =
    `datastocks/${stockUuid}/processes?search=true&name=${encodedQuery}&pageSize=${pageSize}` +
    `&startIndex=${startIndex}&format=json`;
  const data = await fetchJSON(path, { signal });

  const items = Array.isArray(data?.data) ? data.data : [];
  const resolvedStart = Number.isFinite(Number(data?.startIndex)) ? Number(data.startIndex) : startIndex;
  const resolvedPageSize = Number.isFinite(Number(data?.pageSize)) ? Number(data.pageSize) : pageSize;
  const resolvedTotal = Number.isFinite(Number(data?.totalCount)) ? Number(data.totalCount) : items.length;

  const annotatedItems = items.map((item) => ({ ...item, subtype: extractSubtypeFromProcess(item) }));

  const nameMap = new Map();
  if (enrich) {
    try {
      const uuids = annotatedItems.map((it) => it?.uuid).filter(Boolean);
      const got = await enrichProcessNames(uuids, { signal, concurrency: 8 });
      got.forEach((value, key) => nameMap.set(key, value));
    } catch (_) {
      // never throw on enrichment; partial is fine
    }
  }

  const enrichedItems = annotatedItems.map((item, index) => {
    const enrichedName = nameMap.get(item.uuid);
    let normalizedName;

    // KEY: If asking for enrichment (the list pages), only show EN—or nothing—so we can gate on it.
    if (enrich) {
      normalizedName =
        (typeof enrichedName === 'string' && enrichedName.trim().length > 0)
          ? enrichedName.trim()
          : '';
    } else {
      // For direct lookups elsewhere, fall back to the raw item name.
      normalizedName =
        (typeof enrichedName === 'string' && enrichedName.trim().length > 0)
          ? enrichedName.trim()
          : (typeof item?.name === 'string' && item.name.trim().length > 0)
            ? item.name.trim()
            : `Dataset ${index + 1}`;
    }

    return {
      ...item,
      displayName: normalizedName,
      // Keep raw fallback around for UI to use if EN truly doesn't exist
      fallbackName: (item.name || '').trim()
    };
  });

  return {
    data: enrichedItems,
    startIndex: resolvedStart,
    pageSize: resolvedPageSize,
    totalCount: resolvedTotal
  };
}

// Batched English-name enricher with limited concurrency
export async function enrichProcessNames(uuids = [], { signal, concurrency = 6 } = {}) {
  const out = new Map();
  const queue = uuids.filter(Boolean);
  let active = 0;
  let aborted = false;

  const doneOrResolve = (resolve) => resolve(out);

  return await new Promise((resolve) => {
    const pump = () => {
      if (signal?.aborted) aborted = true;
      if ((!queue.length && active === 0) || aborted) return doneOrResolve(resolve);
      while (!aborted && active < concurrency && queue.length) {
        const uuid = queue.shift();
        active++;
        (async () => {
          try {
            let detail = PROCESS_DETAIL_CACHE.get(uuid);
            if (!detail) {
              detail = await fetchJSON(`processes/${uuid}?format=json`, { signal });
              PROCESS_DETAIL_CACHE.set(uuid, detail);
            }
            const name = extractLocalizedProcessName(detail);
            if (name) out.set(uuid, name);
          } catch (_) {
            // ignore per-item errors
          } finally {
            active--;
            pump();
          }
        })();
      }
    };
    pump();
  });
}

export async function loadProcessDetails(processUuid, { ownerFallback, nameFallback } = {}) {
  const compute = async (rawDetail) => {
    const name = extractLocalizedProcessName(rawDetail, nameFallback) || 'Unnamed dataset';
    const manufacturer = extractManufacturer(rawDetail, ownerFallback);
    const declaredUnitInfo = await resolveDeclaredUnit(rawDetail);
    const impacts = parseImpactResults(rawDetail?.LCIAResults?.LCIAResult || []);
    const documentation = extractDocumentation(rawDetail);
    const flowData = declaredUnitInfo?.flowData;
    const declaredUnit = declaredUnitInfo
      ? { flowName: declaredUnitInfo.flowName, property: declaredUnitInfo.property, unit: declaredUnitInfo.unit, amount: declaredUnitInfo.amount }
      : null;
    const materialProperties = extractFlowProperties(rawDetail, flowData);

    return {
      uuid: processUuid,
      name,
      manufacturer,
      declaredUnit,
      impacts,
      subtype: extractSubtypeFromProcess(rawDetail),
      datasetVersion: rawDetail?.administrativeInformation?.publicationAndOwnership?.dataSetVersion || null,
      geography: rawDetail?.processInformation?.geography?.locationOfOperationSupplyOrProduction?.location || null,
      referenceFlowName: declaredUnitInfo?.flowName || declaredUnitInfo?.rawFlowName || null,
      documentation,
      materialProperties
    };
  };

  let processDetail = PROCESS_DETAIL_CACHE.get(processUuid);
  if (!processDetail) {
    processDetail = await fetchJSON(`processes/${processUuid}?format=json`);
    PROCESS_DETAIL_CACHE.set(processUuid, processDetail);
  }

  let result = await compute(processDetail);

  const looksThin = !result.declaredUnit && (!Array.isArray(result.impacts) || result.impacts.length === 0);
  if (looksThin) {
    try {
      const fresh = await fetchJSON(`processes/${processUuid}?format=json&ts=${Date.now()}`);
      PROCESS_DETAIL_CACHE.set(processUuid, fresh);
      result = await compute(fresh);
    } catch (_) {
      // keep previous
    }
  }

  return result;
}

export function formatModules(modules = {}) {
  const ordered = ['A1-A3','A4','A5','B1','B2','B3','B4','B5','B6','B7','C1','C2','C3','C4','D'];
  const result = [];
  ordered.forEach((k) => { if (modules[k] !== undefined) result.push({ key: k, value: modules[k] }); });
  Object.keys(modules).forEach((k) => { if (!ordered.includes(k)) result.push({ key: k, value: modules[k] }); });
  return result;
}
